import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { folderNames, folderOf } from "../stations/station-folders";
import "./create-folder.styles.scss";

// mode: create | move | rename. All persistence belongs to Stations.
export default function CreateFolder({ mode, project, folder, projects, onSave, onClose }) {
  const dialog = useRef(null);
  const saving = useRef(false);
  const names = folderNames(projects);
  const [choice, setChoice] = useState(mode === "move" && names.length ? "existing" : "new");
  const [existing, setExisting] = useState(folderOf(project || {}) || names[0] || "");
  const [name, setName] = useState(mode === "rename" ? folder : "");
  const [selected, setSelected] = useState([]);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const title = mode === "rename" ? "Rename folder" : mode === "move" ? "Move to folder" : "Create folder";

  useEffect(() => {
    const element = dialog.current;
    const previousFocus = document.activeElement;
    element.showModal();
    return () => {
      element.close();
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, []);

  const close = () => { if (!saving.current) onClose(); };
  const submit = async (event) => {
    event.preventDefault();
    if (saving.current) return;
    const target = mode === "move" && choice === "existing" ? existing : name.trim();
    const affected = mode === "move" ? projects.filter((p) => p.stationId === project.stationId)
      : mode === "rename" ? projects.filter((p) => folderOf(p) === folder)
      : projects.filter((p) => selected.includes(p.stationId));
    setError("");
    if (!target.trim()) return setError("Enter a folder name.");
    if (mode === "move" && choice === "existing" && !names.includes(target)) {
      return setError("That folder no longer exists. Choose another folder.");
    }
    if ((mode !== "move" || choice === "new") && names.some(
      (item) => item !== (mode === "rename" ? folder : null) && item.trim().toLocaleLowerCase() === target.toLocaleLowerCase(),
    )) return setError("That folder already exists. Choose it from Move to Folder, or use another name.");
    if (!affected.length) return setError("Select at least one available project.");
    saving.current = true;
    setBusy(true);
    try {
      await onSave(affected, target);
      onClose();
    } catch (err) {
      setError(err.message || "Could not save the folder. Please try again.");
    } finally {
      saving.current = false;
      setBusy(false);
    }
  };

  const visibleProjects = projects.filter((p) => (p.name || "").toLocaleLowerCase().includes(query.toLocaleLowerCase()));
  return createPortal(
    <dialog ref={dialog} className="station-folder-dialog" aria-labelledby="station-folder-title"
      onCancel={(event) => { event.preventDefault(); close(); }}
      onClick={(event) => { if (event.target === event.currentTarget) close(); }}>
      <form onSubmit={submit} onClick={(event) => event.stopPropagation()} aria-busy={busy}>
        <header>
          <h2 id="station-folder-title">{title}</h2>
          <button type="button" aria-label="Close" onClick={close} disabled={busy}>×</button>
        </header>
        {mode === "move" && <p>Move <strong>{project.name || "Untitled project"}</strong>.</p>}
        <fieldset disabled={busy}>
          {mode === "move" && names.length > 0 && (
            <div className="station-folder-dialog__choices">
              <label><input type="radio" name="folder-choice" checked={choice === "existing"} onChange={() => setChoice("existing")} /> Existing folder</label>
              <label><input type="radio" name="folder-choice" checked={choice === "new"} onChange={() => setChoice("new")} /> New folder</label>
            </div>
          )}
          {mode === "move" && choice === "existing" ? (
            <label>Folder
              <select value={existing} onChange={(event) => setExisting(event.target.value)} autoFocus>
                {names.map((item) => <option key={item} value={item}>{item}{item === folderOf(project || {}) ? " (current)" : ""}</option>)}
              </select>
            </label>
          ) : (
            <label>Folder name
              <input value={name} onChange={(event) => setName(event.target.value)} maxLength={80} autoFocus required />
            </label>
          )}
          {mode === "create" && <>
            <p>Select projects for this folder. Empty folders are not saved.</p>
            <label>Find projects<input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search projects…" /></label>
            <div className="station-folder-dialog__projects">
              {visibleProjects.map((item) => (
                <label key={item.stationId}>
                  <input type="checkbox" checked={selected.includes(item.stationId)} onChange={(event) => {
                    const checked = event.target.checked;
                    setSelected((ids) => checked ? [...ids, item.stationId] : ids.filter((id) => id !== item.stationId));
                  }} />
                  <span>{item.emojicon} {item.name || "Untitled project"}<small>{folderOf(item) || "Outside folders"}</small></span>
                </label>
              ))}
              {!visibleProjects.length && <p>No projects found.</p>}
            </div>
            <small>{selected.length} selected</small>
          </>}
        </fieldset>
        {error && <p className="station-folder-dialog__error" role="alert">{error}</p>}
        <footer>
          <button type="button" onClick={close} disabled={busy}>Cancel</button>
          <button type="submit" disabled={busy || (mode === "create" && !selected.length) || (mode === "move" && choice === "existing" && existing === folderOf(project || {}))}>
            {busy ? "Saving…" : mode === "move" ? "Move project" : mode === "rename" ? "Save name" : "Create folder"}
          </button>
        </footer>
      </form>
    </dialog>, document.body,
  );
}
